import { client } from '../utils/fetch';
import { BigDisplay, BigDisplayResponse, PlaybackSettings } from '../types/BigDisplay';

const API_ENDPOINT = 'displays';
export const MAX_DISPLAY_IMAGES_PER_SEND = 50;

export interface SelectImageRequest {
  display_id: number;
  image_url: string[];
}

export interface SelectImageResponse {
  message: string;
  image_urls: string[];
  playback_settings?: PlaybackSettings;
}

interface SelectProductRequest {
  display_id: number;
  product_id: number;
}

interface SelectProductResponse {
  message: string;
  image_urls: string[];
}

export const getBigDisplayData = async ( 
  storeId: string, 
  limit: number, 
  offset: number
): Promise<BigDisplayResponse> => {
  try {
    const params = new URLSearchParams({
      store_id: storeId,
      limit: String(limit),
      offset: String(offset),
    });

    const response = await client.get<BigDisplayResponse>(`${API_ENDPOINT}?${params.toString()}`);
    console.log(response);
    return response;
  } catch (error) {
    console.error("Error fetching display data:", error);
    throw error;
  }
};


export const selectImageForDisplay = async (
  displayId: number,
  imageUrl: string[]
): Promise<SelectImageResponse> => {
  try {
    if (imageUrl.length > MAX_DISPLAY_IMAGES_PER_SEND) {
      throw new Error(`You can send up to ${MAX_DISPLAY_IMAGES_PER_SEND} images at once.`);
    }

    const requestBody: SelectImageRequest = {
      display_id: displayId,
      image_url: imageUrl
    };

    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const response = await client.post<SelectImageResponse>(
      `${API_ENDPOINT}/select-image`,
      requestBody,
      headers
    );
	
    return response;
  } catch (error) {
    console.error("Error selecting image for display:", error);
    throw error;
  }
}; 


export const selectProductForDisplay = async (
  displayId: number,
  productId: number,
): Promise<SelectProductResponse> => {
  try {
    const requestBody: SelectProductRequest = {
      display_id: displayId,
      product_id: productId,
    };

    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const response = await client.post<SelectProductResponse>(
      `${API_ENDPOINT}/select-product`,
      requestBody,
      headers
    );

    return response;
  } catch (error) {
    console.error('Error selecting product for display:', error);
    throw error;
  }
};

export interface SetOrientationRequest {
  display_id: number;
  orientation: 'portrait' | 'landscape';
}

export interface SetOrientationResponse {
  message: string;
}

export const setDisplayOrientation = async (
  displayId: number,
  orientation: 'portrait' | 'landscape'
): Promise<SetOrientationResponse> => {
  try {
    const requestBody: SetOrientationRequest = {
      display_id: displayId,
      orientation,
    };

    const headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    };

    const response = await client.post<SetOrientationResponse>(
      `${API_ENDPOINT}/set-orientation`,
      requestBody,
      headers
    );

    return response;
  } catch (error) {
    console.error('Error setting display orientation:', error);
    throw error;
  }
};

export const getMyDisplays = async (): Promise<BigDisplay[]> => {
  try {
    const response = await client.get<BigDisplayResponse>(`${API_ENDPOINT}/mine`);
    return response.displays ?? [];
  } catch (error) {
    console.error('Error fetching my displays:', error);
    throw error;
  }
};

export interface AddDisplayRequest {
  android_id: string;
  display_type: number;
}

export interface AddDisplayResponse {
  message: string;
  id: number;
}

export const addDisplay = async (androidId: string, displayTypeId: number): Promise<AddDisplayResponse> => {
  try {
    const response = await client.post<AddDisplayResponse>(`${API_ENDPOINT}/add`, {
      android_id: androidId,
      display_type_id: displayTypeId,
    });
    return response;
  } catch (error) {
    console.error('Error adding display:', error);
    throw error;
  }
};

export interface DeleteDisplayResponse {
  message: string;
}

export const deleteDisplay = async (displayId: number): Promise<DeleteDisplayResponse> => {
  try {
    const response = await client.delete<DeleteDisplayResponse>(`${API_ENDPOINT}/${displayId}`, null);
    return response;
  } catch (error) {
    console.error('Error deleting display:', error);
    throw error;
  }
};

export const getDisplaySettings = async (displayId: number): Promise<PlaybackSettings> => {
  try {
    return await client.get<PlaybackSettings>(`${API_ENDPOINT}/${displayId}/settings`);
  } catch (error) {
    console.error('Error fetching display settings:', error);
    throw error;
  }
};

export const updateDisplaySettings = async (
  displayId: number,
  patch: Partial<PlaybackSettings>
): Promise<PlaybackSettings> => {
  try {
    return await client.patch<PlaybackSettings>(
      `${API_ENDPOINT}/${displayId}/settings`,
      patch,
      { 'Content-Type': 'application/json;charset=UTF-8' }
    );
  } catch (error) {
    console.error('Error updating display settings:', error);
    throw error;
  }
};

export const restartDisplay = async (displayId: number): Promise<{ message: string }> => {
  try {
    return await client.post<{ message: string }>(`${API_ENDPOINT}/${displayId}/restart`, {});
  } catch (error) {
    console.error('Error restarting display:', error);
    throw error;
  }
};

export interface CheckDisplayOnlineResponse {
  online: boolean;
  message?: string;
  reason?: string;
  timeout_seconds?: number;
  display_id: number;
  mac: string;
  worker?: {
    sent?: boolean;
    msgid?: string;
    reason?: string;
    mac?: string;
  };
  battery_level: number | null;
  wifi_rssi: number | null;
  battery_updated_at: string | null;
}

export const checkDisplayOnline = async (displayId: number): Promise<CheckDisplayOnlineResponse> => {
  try {
    return await client.post<CheckDisplayOnlineResponse>(`${API_ENDPOINT}/${displayId}/check-online`, {});
  } catch (error) {
    console.error('Error checking display online status:', error);
    throw error;
  }
};

export interface DisplayTypeOption {
  id: number;
  name: string;
  width: number;
  height: number;
}

export const getDisplayTypes = async (): Promise<DisplayTypeOption[]> => {
  try {
    return await client.get<DisplayTypeOption[]>(`${API_ENDPOINT}/types`);
  } catch (error) {
    console.error('Error fetching display types:', error);
    throw error;
  }
};
